import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useQuery } from "@tanstack/react-query";
import useAuth from "../../../hooks/useAuth";
import { fetchOperationsPlannerGrossReport } from "../../../api/operationsPlannerGrossReportService";
import { OperationsPlannerGrossReportDto } from "../../../interfaces/Operations/operationsPlannerGrossReport";
import { operationsGrossTargets } from "../../../data/operationsGrossTargets";

const formatCurrency = (value: number) =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatNumber = (value: number) =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const getDifferenceColor = (difference: number) => {
  if (difference < 0) {
    return "error.main";
  }

  if (difference > 0) {
    return "warning.main";
  }

  return "text.secondary";
};

const buildMonthDates = (month: Dayjs) => {
  const startOfMonth = month.startOf("month");
  const totalDays = startOfMonth.daysInMonth();

  return Array.from({ length: totalDays }, (_, index) =>
    startOfMonth.add(index, "day"),
  );
};

const findTargetsForMonth = (
  month: Dayjs,
): Record<string, { gross: number; rpm: number }> => {
  const availableMonths = Object.keys(operationsGrossTargets)
    .map((key) => dayjs(key))
    .filter((value) => value.isValid())
    .sort((a, b) => b.valueOf() - a.valueOf());

  const matchingMonth = availableMonths.find(
    (value) => value.isSame(month, "month") || value.isBefore(month),
  );

  const fallbackKey =
    matchingMonth?.format("YYYY-MM") ||
    availableMonths.pop()?.format("YYYY-MM");

  if (fallbackKey && operationsGrossTargets[fallbackKey]) {
    return operationsGrossTargets[fallbackKey];
  }

  return {};
};

type AveragedGrossReport = OperationsPlannerGrossReportDto & { title: string };

const buildAverageGrossReport = (
  reports: OperationsPlannerGrossReportDto[],
): AveragedGrossReport | undefined => {
  if (!reports.length) {
    return undefined;
  }

  if (reports.length === 1) {
    return {
      ...reports[0],
      title: dayjs(reports[0].date).format("dddd, MMMM D"),
    };
  }

  const itemAccumulator = new Map<
    string,
    {
      label: string;
      grossSum: number;
      mileageSum: number;
      rpmSum: number;
      count: number;
    }
  >();

  reports.forEach((report) => {
    report.items.forEach((item) => {
      const existing = itemAccumulator.get(item.key) || {
        label: item.label,
        grossSum: 0,
        mileageSum: 0,
        rpmSum: 0,
        count: 0,
      };

      itemAccumulator.set(item.key, {
        label: item.label,
        grossSum: existing.grossSum + item.actualGross,
        mileageSum: existing.mileageSum + item.mileage,
        rpmSum: existing.rpmSum + item.actualRpm,
        count: existing.count + 1,
      });
    });
  });

  const itemOrder = reports[0]?.items.map((item) => item.key) || [];

  const averagedItems = itemOrder.map((key) => {
    const value = itemAccumulator.get(key);

    if (!value) {
      return { key, label: key, actualGross: 0, mileage: 0, actualRpm: 0 };
    }

    return {
      key,
      label: value.label,
      actualGross: Number((value.grossSum / value.count).toFixed(2)),
      mileage: Number((value.mileageSum / value.count).toFixed(2)),
      actualRpm: Number((value.rpmSum / value.count).toFixed(2)),
    };
  });

  const companyTotals = reports.reduce(
    (acc, report) => ({
      gross: acc.gross + report.companyTotal.actualGross,
      mileage: acc.mileage + report.companyTotal.mileage,
      rpm: acc.rpm + report.companyTotal.actualRpm,
    }),
    { gross: 0, mileage: 0, rpm: 0 },
  );

  const averageDivider = reports.length || 1;

  return {
    title: `Average gross across ${reports.length} days`,
    date: reports[0].date,
    items: averagedItems,
    companyTotal: {
      key: "companyTotal",
      label: "Company total",
      actualGross: Number((companyTotals.gross / averageDivider).toFixed(2)),
      mileage: Number((companyTotals.mileage / averageDivider).toFixed(2)),
      actualRpm: Number((companyTotals.rpm / averageDivider).toFixed(2)),
    },
  };
};

const OperationsGrossReport = () => {
  const { token } = useAuth();
  const [selectedDates, setSelectedDates] = useState<Dayjs[]>([
    dayjs().startOf("day"),
  ]);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().startOf("month"));

  const monthDates = useMemo(
    () => buildMonthDates(selectedMonth),
    [selectedMonth],
  );

  const sortedSelectedDates = useMemo(
    () =>
      [...selectedDates].sort(
        (first, second) => first.valueOf() - second.valueOf(),
      ),
    [selectedDates],
  );

  const selectedDateKeys = useMemo(
    () => sortedSelectedDates.map((date) => date.format("YYYY-MM-DD")),
    [sortedSelectedDates],
  );

  const selectedDateRangeLabel = useMemo(() => {
    if (!sortedSelectedDates.length) {
      return "";
    }

    if (sortedSelectedDates.length === 1) {
      return dayjs(sortedSelectedDates[0]).format("dddd, MMMM D");
    }

    const start = sortedSelectedDates[0];
    const end = sortedSelectedDates[sortedSelectedDates.length - 1];

    return `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`;
  }, [sortedSelectedDates]);

  const setMonthOffset = (offset: number) => {
    setSelectedMonth((prev) => prev.add(offset, "month"));
  };

  const handleMonthChange = (value: Dayjs | null) => {
    if (!value) return;

    setSelectedMonth(value.startOf("month"));
  };

  const handleSelectFullMonth = () => {
    setSelectedDates(monthDates.map((date) => date.startOf("day")));
  };

  const handleClearSelection = () => {
    setSelectedDates([]);
  };

  const handleDayToggle = (value: Dayjs) => {
    setSelectedDates((prev) => {
      const normalized = value.startOf("day");
      const exists = prev.some((item) => item.isSame(normalized, "day"));

      if (exists) {
        if (prev.length === 1) {
          return prev;
        }

        return prev.filter((item) => !item.isSame(normalized, "day"));
      }

      return [...prev, normalized];
    });
  };

  const {
    data: reports,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery<OperationsPlannerGrossReportDto[], Error>({
    queryKey: ["operations-gross-report", selectedDateKeys],
    queryFn: () =>
      Promise.all(
        selectedDateKeys.map((date) =>
          fetchOperationsPlannerGrossReport(token, date),
        ),
      ),
    enabled: Boolean(token) && selectedDateKeys.length > 0,
    placeholderData: (previousData) => previousData,
  });

  const averagedReport = useMemo(
    () => (reports ? buildAverageGrossReport(reports) : undefined),
    [reports],
  );

  const monthDays = selectedMonth.daysInMonth();

  const monthlyTargets = useMemo(
    () => findTargetsForMonth(selectedMonth),
    [selectedMonth],
  );

  const getDailyTargetForKey = (key: string) => {
    const monthlyTarget = monthlyTargets[key]?.gross ?? 0;
    return monthlyTarget / monthDays;
  };

  const getRpmTargetForKey = (key: string) => {
    return monthlyTargets[key]?.rpm ?? 0;
  };

  const calculateDifference = (projected: number, actual: number) =>
    actual - projected;

  const companyTargetPerDay = useMemo(() => {
    if (!averagedReport) return 0;

    return averagedReport.items.reduce((acc, item) => {
      return acc + getDailyTargetForKey(item.key);
    }, 0);
  }, [averagedReport, monthlyTargets, monthDays]);

  const companyTargetRpm = useMemo(() => {
    if (!averagedReport) return 0;

    const totalRpm = averagedReport.items.reduce((acc, item) => {
      return acc + getRpmTargetForKey(item.key);
    }, 0);

    return totalRpm / (averagedReport.items.length || 1);
  }, [averagedReport, monthlyTargets]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0, sm: 3 }, minWidth: 0 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        mb={3}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ minWidth: 0 }}
        >
          <AssessmentOutlinedIcon color="primary" />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" fontWeight={600}>
              Operations gross report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View single-day results or average gross metrics across multiple
              selections. Monthly targets are converted to daily goals for
              comparison against actual gross totals.
            </Typography>
          </Box>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ maxWidth: "100%" }}
        >
          <IconButton
            aria-label="Previous month"
            onClick={() => setMonthOffset(-1)}
          >
            <ArrowBackIosNewRoundedIcon fontSize="small" />
          </IconButton>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Month"
              value={selectedMonth}
              onChange={handleMonthChange}
              views={["year", "month"]}
              format="MMMM YYYY"
              slotProps={{
                textField: {
                  size: "small",
                  sx: { width: { xs: 172, sm: 220 }, maxWidth: "100%" },
                },
              }}
            />
          </LocalizationProvider>
          <IconButton aria-label="Next month" onClick={() => setMonthOffset(1)}>
            <ArrowForwardIosRoundedIcon fontSize="small" />
          </IconButton>
          <Tooltip title="Refresh">
            <span>
              <IconButton
                aria-label="Refresh report"
                onClick={() => refetch()}
                disabled={isFetching}
              >
                <RefreshRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            onClick={handleClearSelection}
          >
            Clear selection
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSelectFullMonth}
          >
            Select full month
          </Button>
        </Stack>
      </Stack>

      {isFetching && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          mb={2}
          color="text.secondary"
        >
          <CircularProgress size={18} thickness={4} />
          <Typography variant="body2">Loading latest data...</Typography>
        </Stack>
      )}

      <Box
        className="report-date-picker"
        display="flex"
        flexWrap="wrap"
        gap={1}
        mb={3}
      >
        {monthDates.map((date) => {
          const key = date.format("YYYY-MM-DD");
          const isSelected = sortedSelectedDates.some((item) =>
            item.isSame(date, "day"),
          );

          return (
            <Button
              key={key}
              variant={isSelected ? "contained" : "outlined"}
              color={isSelected ? "primary" : "inherit"}
              onClick={() => handleDayToggle(date)}
              size="small"
              sx={{
                minWidth: 80,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 1,
              }}
            >
              <Typography variant="caption" sx={{ textTransform: "uppercase" }}>
                {date.format("ddd")}
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {date.format("D")}
              </Typography>
            </Button>
          );
        })}
      </Box>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.message || "Failed to load operations gross report."}
        </Alert>
      )}

      {!averagedReport ? (
        <Card>
          <CardHeader title={selectedDateRangeLabel || "No dates selected"} />
          <CardContent>
            <Typography color="text.secondary">
              Select one or more days to view gross performance.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader
            title={selectedDateRangeLabel}
            subheader={averagedReport.title}
          />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TableContainer
                  role="region"
                  aria-label="Operations gross report table"
                  tabIndex={0}
                >
                  <Table sx={{ minWidth: 900 }} size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Category</TableCell>
                        <TableCell align="right">
                          Target gross (per day)
                        </TableCell>
                        <TableCell align="right">Actual gross</TableCell>
                        <TableCell align="right">Gross difference</TableCell>
                        <TableCell align="right">Target RPM</TableCell>
                        <TableCell align="right">Actual RPM</TableCell>
                        <TableCell align="right">RPM difference</TableCell>
                        <TableCell align="right">Actual mileage</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {averagedReport.items.map((item) => {
                        const targetGrossPerDay = getDailyTargetForKey(
                          item.key,
                        );
                        const differenceGross = calculateDifference(
                          targetGrossPerDay,
                          item.actualGross,
                        );
                        const targetRpm = getRpmTargetForKey(item.key);
                        const differenceRpm = calculateDifference(
                          targetRpm,
                          item.actualRpm,
                        );

                        return (
                          <TableRow key={item.key} hover>
                            <TableCell>{item.label}</TableCell>
                            <TableCell align="right">
                              ${formatCurrency(targetGrossPerDay)}
                            </TableCell>
                            <TableCell align="right">
                              ${formatCurrency(item.actualGross)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                color: getDifferenceColor(differenceGross),
                              }}
                            >
                              ${formatCurrency(differenceGross)}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(targetRpm)}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(item.actualRpm)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: getDifferenceColor(differenceRpm) }}
                            >
                              {formatCurrency(differenceRpm)}
                            </TableCell>
                            <TableCell align="right">
                              {formatNumber(item.mileage)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow sx={{ backgroundColor: "action.hover" }}>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Company total
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          ${formatCurrency(companyTargetPerDay)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          $
                          {formatCurrency(
                            averagedReport.companyTotal.actualGross,
                          )}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 700,
                            color: getDifferenceColor(
                              calculateDifference(
                                companyTargetPerDay,
                                averagedReport.companyTotal.actualGross,
                              ),
                            ),
                          }}
                        >
                          $
                          {formatCurrency(
                            calculateDifference(
                              companyTargetPerDay,
                              averagedReport.companyTotal.actualGross,
                            ),
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {formatCurrency(companyTargetRpm)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {formatCurrency(
                            averagedReport.companyTotal.actualRpm,
                          )}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 700,
                            color: getDifferenceColor(
                              calculateDifference(
                                companyTargetRpm,
                                averagedReport.companyTotal.actualRpm,
                              ),
                            ),
                          }}
                        >
                          {formatCurrency(
                            calculateDifference(
                              companyTargetRpm,
                              averagedReport.companyTotal.actualRpm,
                            ),
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {formatNumber(averagedReport.companyTotal.mileage)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default OperationsGrossReport;
